import { alfred, Condition } from '@api';
import { deepEqual } from '@helpers';
import { config } from '@terminal/config';
import { apiStream, ApiStreamStatus } from '@terminal/utils/api-stream/apiStream';
import { generateConditionString } from '@terminal/utils/condition-utils/condition-utils';
import { create } from '@terminal/utils/store/createStore';
import { useScenarioStore } from '@terminal/widgets/scenario/hooks/use-scenario-store/useScenarioStore';
import { TFunction } from 'i18next';
import { v4 } from 'uuid';

import {
  normalizeMinMaxValues,
  validateCondition,
} from '../validate-condition/validateCondition';
import { enrichedConditions, MappedConditions } from './enrichedConditions';

export interface ConditionWithLabel extends Condition {
  label: string;
}

type ScenarioAssistantStore = {
  askedQuestion: string;
  editingConditionId?: string;
  sendMessage: (question: string) => Promise<void>;
  abortCurrentRequest: () => void;
  manuallyAddCustomCondition: (
    t: TFunction,
    condition: ConditionWithLabel
  ) => Promise<void>;
  manuallySetCustomConditions: (
    conditions: ConditionWithLabel[]
  ) => Promise<void>;
  resetConditions: () => void;
  deleteCustomCondition: (id: string) => void;
  updateCustomCondition: (condition: MappedConditions) => void;
  confirmConditions: () => void;
  runConfirmedConditions: () => void;
  toggleConditionState: (id: string) => void;
  resetAskedQuestions: () => void;
  editQuestion: (id?: MappedConditions['id']) => void;
  normalizeConditions: () => void;
  conditions: MappedConditions[];
  loading: boolean;
  error: boolean;
  conditionIdsFromCurrentRequest: string[];
  currentRequestId: string | null;
  abortController: AbortController | null;
};

export const useScenarioAssistant = create<ScenarioAssistantStore>(
  (set, get) => ({
    askedQuestion: '',
    editingConditionId: undefined,
    conditions: [],
    loading: false,
    error: false,
    conditionIdsFromCurrentRequest: [],
    currentRequestId: null,
    abortController: null,

    abortCurrentRequest: () => {
      const state = get();
      if (state.abortController) {
        state.abortController.abort();
      }
      const keptConditions = state.conditions.filter(
        c => !state.conditionIdsFromCurrentRequest.includes(c.id)
      );
      set(() => ({
        conditions: keptConditions,
        conditionIdsFromCurrentRequest: [],
        currentRequestId: null,
        abortController: null,
        loading: false,
        editingConditionId: undefined,
      }));
    },

    updateCustomCondition: async condition => {
      set(state => {
        const existingCondition = state.conditions.find(
          c => c.id === condition.id
        );

        if (!existingCondition) {
          return state;
        }

        const hasConditionChanged = !deepEqual(existingCondition, condition);

        if (!hasConditionChanged) {
          return state;
        }

        return {
          ...state,
          conditions: state.conditions.map(c =>
            c.id === condition.id
              ? {
                  ...c,
                  ...condition,
                  isValid: validateCondition(condition),
                  display: c.label ? c.label : undefined,
                }
              : c
          ),
        };
      });

      const { setLastConditions } = useScenarioStore.getState();
      const updatedConditions = get()
        .conditions.filter(c => c.confirmed && c.isValid && c.active)
        .map(
          ({
            pendingRun,
            confirmed,
            isValid,
            askedQuestion,
            display,
            ...rest
          }) => rest
        );

      setLastConditions(updatedConditions);
    },
    confirmConditions: () => {
      set(state => ({
        conditions: state.conditions
          .filter(c => c.active)
          .map(c => ({
            ...c,
            confirmed: true,
            pendingRun: true,
          })),
        conditionIdsFromCurrentRequest: [],
        currentRequestId: null,
      }));
    },
    runConfirmedConditions: () => {
      set(state => ({
        conditions: state.conditions.map(c => ({ ...c, pendingRun: false })),
      }));
    },
    toggleConditionState: id => {
      set(state => ({
        conditions: state.conditions.map(c =>
          c.id === id ? { ...c, active: !c.active } : c
        ),
      }));
    },

    deleteCustomCondition: id => {
      set(state => ({ conditions: state.conditions.filter(c => c.id !== id) }));
    },

    sendMessage: async (askedQuestion: string) => {
      const controller = new AbortController();
      const id = v4();

      set({
        loading: true,
        askedQuestion,
        currentRequestId: id,
        conditionIdsFromCurrentRequest: [],
        abortController: controller,
      });

      const onComplete = (status: ApiStreamStatus) => {
        if (
          status === ApiStreamStatus.Success ||
          status === ApiStreamStatus.Abort
        ) {
          set(state => ({
            loading: false,
            error: !state.conditions.length,
            editingConditionId: undefined,
            currentRequestId: null,
            abortController: null,
          }));
        }

        if (status === ApiStreamStatus.Error) {
          set({
            loading: false,
            error: true,
            currentRequestId: null,
            abortController: null,
            conditionIdsFromCurrentRequest: [],
          });
        }
      };

      const onReadChunk = async (
        reply: { data: { conditions: Condition[] } }[]
      ) => {
        if (reply.length && get().currentRequestId === id) {
          const conditions = await Promise.all(
            reply[0].data.conditions.map(c =>
              enrichedConditions({ condition: c, askedQuestion })
            )
          );

          const newConditionIds = conditions.map(c => c.id);

          set(state => {
            if (state.currentRequestId !== id) {
              return state;
            }
            const nextConditions = state.editingConditionId
              ? state.conditions.reduce<MappedConditions[]>((res, c) => {
                  if (c.id === state.editingConditionId) {
                    res.push(...conditions);
                  } else {
                    res.push(c);
                  }
                  return res;
                }, [])
              : [...state.conditions, ...conditions];
            const nextIdsFromRequest = [
              ...state.conditionIdsFromCurrentRequest,
              ...newConditionIds,
            ];
            return {
              ...state,
              conditions: nextConditions,
              conditionIdsFromCurrentRequest: nextIdsFromRequest,
            };
          });
        }
      };

      apiStream({
        body: {
          question: askedQuestion,
          session_id: id,
          target: 'scenario-assistant.conditions',
        },
        onReadChunk,
        onComplete: () => onComplete(ApiStreamStatus.Success),
        onError: () => onComplete(ApiStreamStatus.Error),
        onAbort: () => onComplete(ApiStreamStatus.Abort),
        controller,
        endpoint: `${config.api.root}${alfred.streaming.path}`,
      });
    },
    manuallyAddCustomCondition: async (t, condition) => {
      const { setLastConditions } = useScenarioStore.getState();
      const { conditions } = useScenarioAssistant.getState();
      const mappedCondition = await enrichedConditions({
        condition,
        isDefaultConfirmed: true,
      });
      const conditionString = generateConditionString({
        t,
        conditions: [mappedCondition],
      });
      const { id: newId, ...newCondition } = mappedCondition;
      const existedCustomCondition = conditions.some(c => {
        const { id, ...existedCondition } = c;
        return deepEqual(newCondition, existedCondition);
      });

      if (existedCustomCondition) {
        return;
      }

      const { confirmed, isValid, pendingRun, ...rest } = mappedCondition;
      setLastConditions([rest]);
      set(state => ({
        conditions: [...state.conditions, mappedCondition],
        askedQuestion: conditionString,
      }));
    },

    manuallySetCustomConditions: async conditions => {
      const { setLastConditions } = useScenarioStore.getState();
      const mappedConditions = await Promise.all(
        conditions.map(c =>
          enrichedConditions({ condition: c, isDefaultConfirmed: true })
        )
      );
      setLastConditions(
        mappedConditions.map(c => {
          const { confirmed, isValid, pendingRun, ...rest } = c;
          return rest;
        })
      );
      set(() => ({
        conditions: mappedConditions,
      }));
    },
    resetConditions: () => {
      set({
        error: false,
        conditions: [],
      });
    },
    resetAskedQuestions: () => {
      set({ askedQuestion: '', error: false });
    },
    editQuestion: id => {
      set({ editingConditionId: id });
    },
    normalizeConditions: () => {
      const { conditions } = get();
      const normalizedConditions = conditions.map(c =>
        normalizeMinMaxValues(c)
      ) as MappedConditions[];
      set({ conditions: normalizedConditions });
    },
  })
);
